import * as core from '../../../core';
import { inferParsedObject } from '../../../core/schemas';
import {
    jsonSchemaCredentialSubjectSchema,
    VcdmCredentialDocument,
    vcdmDocumentSchema,
    vcdmProofSetSchema,
} from '../document/vcdm';
import type {
    CredentialBlob,
    CredentialClaims,
    CredentialMetadata,
    CredentialSchema,
    DocumentFlavor,
    Json,
} from '../types';

export const vcdm11DocumentSchema = core.serialization
    .object({
        id: core.serialization.string(),
        holder: core.serialization.string().optional(),
        issuer: core.serialization.string(),
        issuanceDate: core.serialization.date(),
        expirationDate: core.serialization.date().optional(),
        proof: vcdmProofSetSchema,
    })
    .extend(vcdmDocumentSchema);

export type Vcdm11Document = inferParsedObject<typeof vcdm11DocumentSchema>;

export class Vcdm11Flavor extends VcdmCredentialDocument implements DocumentFlavor {
    kind = 'vcdm1_1';

    private blobToVcdm11(input: CredentialBlob): Promise<Vcdm11Document> {
        return this.parseBlob(vcdm11DocumentSchema, input);
    }

    private async loadJsonDocument(url: string): Promise<Json | undefined> {
        return fetch(url)
            .then((resp) => {
                if (!resp.ok)
                    return Promise.reject(
                        new Error(
                            `Failed to fetch JSON document from ${url}. Server responded with status: ${resp.status} (${resp.statusText})`,
                        ),
                    );
                return resp.json();
            })
            .catch((err) => {
                console.warn(`Error loading JSON document from ${url}: ${err}`);
                return undefined;
            });
    }

    async isOfFlavor(blob: CredentialBlob): Promise<boolean> {
        try {
            await this.blobToVcdm11(blob);
        } catch {
            return false;
        }
        return true;
    }

    async getClaims(blob: CredentialBlob): Promise<CredentialClaims> {
        const credential = await this.blobToVcdm(blob);
        const { id: _id, type: _type, ...claims } = credential.credentialSubject;

        return claims as CredentialClaims;
    }

    async getMetadata(blob: CredentialBlob): Promise<CredentialMetadata> {
        const credential = await this.blobToVcdm11(blob);

        return {
            id: credential.id,
            issuer: credential.issuer,
            validFrom: credential.issuanceDate,
            validUntil: credential.expirationDate,
            subject: credential.credentialSubject.id,
            holder: credential.holder,
        };
    }

    async getSchema(blob: CredentialBlob): Promise<CredentialSchema | undefined> {
        const credential = await this.blobToVcdm(blob);

        if (!credential.credentialSchema) return undefined;

        const schemaTypes = Array.isArray(credential.credentialSchema.type)
            ? credential.credentialSchema.type
            : [credential.credentialSchema.type];

        if (schemaTypes.includes('JsonSchema')) {
            // External JSON Schema
            return this.loadJsonDocument(credential.credentialSchema.id);
        } else if (schemaTypes.includes('JsonSchemaCredential')) {
            let schemaCredential: Json;

            if (schemaTypes.includes('VerifiableCredential')) {
                // Inlined credential with JSON Schema
                schemaCredential = credential.credentialSchema;
            } else {
                // External credential with JSON Schema
                const result = await this.loadJsonDocument(credential.credentialSchema.id);
                if (!result) return undefined;
                schemaCredential = result;
            }

            try {
                const jsonSchemaCredential = this.jsonToVcdm(schemaCredential);
                const jsonSchemaCredentialClaims = this.parseJson(
                    jsonSchemaCredentialSubjectSchema,
                    jsonSchemaCredential.credentialSubject,
                );

                return jsonSchemaCredentialClaims.jsonSchema;
            } catch {
                console.warn(`Could not parse credential schema: ${credential.credentialSchema.id}`);
                return undefined;
            }
        }
    }
}
